import React, { useState, useEffect } from 'react';
import Menu from '../menu/Menu';
import Table from '../common/Table';
import RTSpinner from '../common/RTSpinner';
import RTButton from '../common/RTButton';
import UpdateAllocationStatusModal from './UpdateAllocationStatusModal';
import { apiGet } from '../../api/apiClient';
import { navigateToPath } from '../../utils/navigationHelper';
import { formatDateToDDMMYYYY } from '../../utils/dateHelper';
import { getStatusDisplay } from '../../utils/allocationStatusUtils';
import './ViewAllocatedBeds.css';

const ViewAllocatedBeds = () => {
  const [loading, setLoading] = useState(true);
  const [bedSummary, setBedSummary] = useState({
    total_beds: 0,
    total_allocated: 0,
    total_vacant: 0
  });
  const [allocations, setAllocations] = useState([]);
  const [error, setError] = useState(null);
  const [statusModal, setStatusModal] = useState({
    isOpen: false,
    mode: '', // 'start', 'cancel', 'end'
    allocation: null
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch bed summary statistics
      const bedResponse = await apiGet('/beds');
      setBedSummary(bedResponse.data.summary);

      // Fetch allocation data
      const allocationResponse = await apiGet('/allocation?status=FutureAllocation,Allocated');
      setAllocations(allocationResponse.data);

    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to load allocation data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Check if start date is today or in the past
  const isStartDateReached = (startDate) => {
    if (!startDate) return false;
    const today = new Date();
    const start = new Date(startDate);
    today.setHours(0, 0, 0, 0);
    start.setHours(0, 0, 0, 0);
    return start <= today;
  };

  // Check if end date is in the past
  const isEndDatePassed = (endDate) => {
    if (!endDate) return false;
    const today = new Date();
    const end = new Date(endDate);
    today.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    return end < today;
  };

  // Handle start allocation
  const handleStartAllocation = (allocation) => {
    setStatusModal({
      isOpen: true,
      mode: 'start',
      allocation: allocation
    });
  };

  // Handle cancel allocation
  const handleCancelAllocation = (allocation) => {
    setStatusModal({
      isOpen: true,
      mode: 'cancel',
      allocation: allocation
    });
  };

  // Handle end allocation
  const handleEndAllocation = (allocation) => {
    setStatusModal({
      isOpen: true,
      mode: 'end',
      allocation: allocation
    });
  };

  // Update allocation status
  const updateAllocationStatus = async () => {
    try {
      setLoading(true);
      
      // Refresh data after update
      await fetchData();
      
      // Close modal
      setStatusModal({ isOpen: false, mode: '', allocation: null });
      
    } catch (error) {
      console.error('Error refreshing data:', error);
      setError('Failed to refresh data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle modal success
  const handleModalSuccess = async () => {
    // Refresh data and close modal
    await fetchData();
    setStatusModal({ isOpen: false, mode: '', allocation: null });
  };

  // Close modal
  const closeModal = () => {
    setStatusModal({ isOpen: false, mode: '', allocation: null });
  };

  // Calculate beds vacating in next 30 days
  const getBedsVacatingNext30Days = () => {
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
    
    return allocations.filter(allocation => {
      if (!allocation.end_date) return false;
      const endDate = new Date(allocation.end_date);
      const today = new Date();
      return endDate >= today && endDate <= thirtyDaysFromNow;
    }).length;
  };

  // Calculate total monthly rent
  const getTotalMonthlyRent = () => {
    return allocations.reduce((total, allocation) => {
      return total + (allocation.rent_amount || 0);
    }, 0);
  };

  const handlePGClick = (pgId) => {
    window.open(`/paying-guest/view/${pgId}`, '_blank');
  };

  const handleBuildingClick = (buildingId) => {
    window.open(`/building/view/${buildingId}`, '_blank');
  };

  const handleViewAllocation = (allocationId) => {
    navigateToPath(`/bedallocation/allocation/${allocationId}`);
  };

  // Define table columns
  const columns = [
    {
      Header: 'Start Date',
      accessor: 'start_date',
      Cell: ({ value }) => formatDateToDDMMYYYY(value)
    },
    {
      Header: 'End Date',
      accessor: 'end_date',
      Cell: ({ value }) => formatDateToDDMMYYYY(value)
    },
    {
      Header: 'PG Name',
      accessor: 'pg_name',
      Cell: ({ value, row }) => (
        <RTButton
          type="link"
          onClick={() => handlePGClick(row.pg_id)}
          className="rt-link-button"
        >
          {value || 'N/A'}
        </RTButton>
      )
    },
    {
      Header: 'Building',
      accessor: 'building_name',
      Cell: ({ value, row }) => (
        <RTButton
          type="link"
          onClick={() => handleBuildingClick(row.building_id)}
          className="rt-link-button"
        >
          {value || 'N/A'}
        </RTButton>
      )
    },
    {
      Header: 'Mobile',
      accessor: 'pg_mobile'
    },
    {
      Header: 'Room',
      accessor: 'room_type'
    },
    {
      Header: 'Advance Amount',
      accessor: 'advance_amount',
      Cell: ({ row }) => {
        const advanceAmount = row.advance_amount || 0;
        const advanceRemaining = row.advance_remaining || 0;
        
        // Show red X if advance is 0 or if there's remaining advance
        // Show green tick if advance > 0 and no remaining advance
        const showRedX = advanceAmount === 0 || advanceRemaining > 0;
        const hasRemaining = advanceRemaining > 0;
        
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span>₹{advanceAmount.toLocaleString()}</span>
            {showRedX ? (
              <span style={{ color: '#d32f2f', fontSize: '18px', fontWeight: 'bold' }} title="Advance not fully paid">✗</span>
            ) : (
              <span style={{ color: '#4caf50', fontSize: '18px', fontWeight: 'bold' }} title="Advance fully paid">✓</span>
            )}
          </div>
        );
      }
    },
    {
      Header: 'Rent Amount',
      accessor: 'rent_amount',
      Cell: ({ value }) => value ? `₹${value.toLocaleString()}` : 'N/A'
    },
    {
      Header: 'Status',
      accessor: 'status',
      Cell: ({ value }) => {
        const getStatusBadge = (status) => {
          const baseStyle = {
            padding: '4px 12px',
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: '600',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            border: 'none',
            display: 'inline-block'
          };

          switch (status) {
            case 'Allocated':
              return {
                ...baseStyle,
                backgroundColor: '#d4edda',
                color: '#155724'
              };
            case 'FutureAllocation':
              return {
                ...baseStyle,
                backgroundColor: '#fff3cd',
                color: '#856404'
              };
            case 'AllocationEnded':
              return {
                ...baseStyle,
                backgroundColor: '#f8d7da',
                color: '#721c24'
              };
            default:
              return {
                ...baseStyle,
                backgroundColor: '#e2e3e5',
                color: '#495057'
              };
          }
        };

        return (
          <span style={getStatusBadge(value)}>
            {getStatusDisplay(value)}
          </span>
        );
      }
    },
    {
      Header: 'Actions',
      accessor: 'actions',
      Cell: ({ row }) => {
        const isFutureAllocation = row.status === 'FutureAllocation';
        const isAllocated = row.status === 'Allocated';
        const canStart = isFutureAllocation && isStartDateReached(row.start_date);
        const canEnd = isAllocated && row.end_date && isEndDatePassed(row.end_date);
        
        return (
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {canStart && (
              <RTButton
                type="link"
                onClick={() => handleStartAllocation(row)}
                style={{ fontWeight: 'bold' }}
              >
                Start Allocation
              </RTButton>
            )}
            
            {canEnd && (
              <RTButton
                type="link"
                onClick={() => handleEndAllocation(row)}
                style={{ fontWeight: 'bold'}}
              >
                End Allocation
              </RTButton>
            )}
            
            {isFutureAllocation && (
              <RTButton
                type="link"
                onClick={() => handleCancelAllocation(row)}
                style={{ fontWeight: 'bold' }}
              >
                Cancel Allocation
              </RTButton>
            )}
            
            <RTButton
              type="link"
              onClick={() => handleViewAllocation(row.allocation_id)}
              style={{ fontWeight: 'bold'}}
            >
              View
            </RTButton>
          </div>
        );
      }
    }
  ];

  if (loading) {
    return <RTSpinner message="Loading allocation data..." fullScreen={true} />;
  }

  return (
    <div className="rt-layout">
      <Menu />
      <div className="rt-main-content">
        <div className="rt-title-row">
          <h2 className="rt-title">View Allocated Beds</h2>
          <div className="rt-actions">
            <RTButton variant="secondary" onClick={() => window.history.back()}>
              &#8592; Back
            </RTButton>
          </div>
        </div>

        {error && (
          <div className="rt-error-message">
            {error}
            <RTButton 
              type="primary" 
              onClick={fetchData}
              style={{ marginLeft: '10px' }}
            >
              Retry
            </RTButton>
          </div>
        )}

        {/* Summary Statistics */}
        <div className="rt-summary-section">
          <div className="rt-summary-table">
            <h4 style={{ color: '#153C7F', marginBottom: '16px' }}>Bed Allocation Summary</h4>
            <div>
              <div style={{ display: 'flex', marginBottom: '8px' }}>
                <div style={{ flex: 1, marginRight: '20px' }}>
                  <label style={{ fontWeight: 'bold', marginRight: '8px' }}>Total Beds:</label>
                  <span>{bedSummary.total_beds}</span>
                </div>
                <div style={{ flex: 1, marginRight: '20px' }}>
                  <label style={{ fontWeight: 'bold', marginRight: '8px' }}>Allocated Beds:</label>
                  <span>{bedSummary.total_allocated}</span>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontWeight: 'bold', marginRight: '8px' }}>Vacant Beds:</label>
                  <span>{bedSummary.total_vacant}</span>
                </div>
              </div>
              <div style={{ display: 'flex', marginBottom: '8px' }}>
                <div style={{ flex: 1, marginRight: '20px' }}>
                  <label style={{ fontWeight: 'bold', marginRight: '8px' }}>Occupancy Rate:</label>
                  <span>
                    {bedSummary.total_beds > 0 
                      ? `${Math.round((bedSummary.total_allocated / bedSummary.total_beds) * 100)}%`
                      : '0%'
                    }
                  </span>
                </div>
                <div style={{ flex: 1, marginRight: '20px' }}>
                  <label style={{ fontWeight: 'bold', marginRight: '8px' }}>Beds Vacating in Next 30 Days:</label>
                  <span>{getBedsVacatingNext30Days()}</span>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontWeight: 'bold', marginRight: '8px' }}>Total Monthly Rent:</label>
                  <span>₹{getTotalMonthlyRent().toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Allocation Details Table */}
        <div className="rt-table-section">
          <Table
            title="Allocation Details"
            columns={columns}
            data={allocations}
            showSearch={true}
            exportable={true}
            showPagination={true}
            noDataText="No bed allocations found."
          />
        </div>
      </div>
      
      {/* Status Update Modal */}
      <UpdateAllocationStatusModal
        isOpen={statusModal.isOpen}
        mode={statusModal.mode}
        allocation={statusModal.allocation}
        onSuccess={handleModalSuccess}
        onCancel={closeModal}
      />
    </div>
  );
};

export default ViewAllocatedBeds;